import { useState, useEffect } from 'react';
import { User, Mail, Building2, MapPin, Briefcase, Users, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { userApi } from '../../services/api';

interface SupervisorInfo {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [supervisor, setSupervisor] = useState<SupervisorInfo | null>(null);
  const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(false);

  useEffect(() => {
    const fetchSupervisor = async () => {
      if (!user?.supervisorId) return;

      setIsLoadingSupervisor(true);
      try {
        const supervisorData = await userApi.getUser(user.supervisorId) as SupervisorInfo;
        setSupervisor(supervisorData);
      } catch (err) {
        console.error('Error fetching supervisor:', err);
      } finally {
        setIsLoadingSupervisor(false);
      }
    };

    fetchSupervisor();
  }, [user?.supervisorId]);

  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Determine who approves this user's leave
  const getLeaveApproverInfo = () => {
    if (user.role === 'DIRECTOR') {
      return {
        title: 'Board Approvers',
        description: 'Your leave is approved by the Board Chairperson or Vice-Chairperson',
        names: ['Michael Vincer (Board Chair)', 'Dr Yolanda Kennedy (Board Vice-Chair)'],
      };
    }
    if (user.reportsDirectlyToDirector) {
      return {
        title: 'Director Approval',
        description: 'Your leave is approved directly by the Director',
        names: ['Kenwin Lombaard (Director)'],
      };
    }
    if (supervisor) {
      return {
        title: 'Supervisor Recommendation',
        description: 'Your leave is recommended by your supervisor, then approved by the Director',
        names: [`${supervisor.firstName} ${supervisor.lastName} (${supervisor.jobTitle || 'Supervisor'})`],
      };
    }
    return null;
  };

  const approverInfo = getLeaveApproverInfo();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-sm text-gray-600">Your account information and leave reporting structure</p>
      </div>

      {/* Personal Information */}
      <div className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Personal Information</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary-100 p-2">
              <User className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Full Name</p>
              <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="rounded-full bg-blue-100 p-2">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{user.email}</p>
            </div>
          </div>

          {user.employeeNumber && (
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-purple-100 p-2">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Employee Number</p>
                <p className="font-medium text-gray-900">{user.employeeNumber}</p>
              </div>
            </div>
          )}

          {user.department && (
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-green-100 p-2">
                <Building2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium text-gray-900">{user.department}</p>
              </div>
            </div>
          )}

          {user.site && (
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-orange-100 p-2">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Site / Location</p>
                <p className="font-medium text-gray-900">{user.site}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="rounded-full bg-yellow-100 p-2">
              <Users className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Role</p>
              <p className="font-medium text-gray-900">
                {user.role.charAt(0) + user.role.slice(1).toLowerCase().replace(/_/g, ' ')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Approval Chain */}
      <div className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Leave Approval</h2>
        <p className="mb-4 text-sm text-gray-600">
          When you submit a leave request, it goes through the following approval process:
        </p>

        {isLoadingSupervisor ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading supervisor information...</span>
          </div>
        ) : approverInfo ? (
          <div className="rounded-lg bg-gray-50 p-4">
            <h3 className="font-medium text-gray-900">{approverInfo.title}</h3>
            <p className="mt-1 text-sm text-gray-600">{approverInfo.description}</p>
            <div className="mt-3 space-y-2">
              {approverInfo.names.map((name, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-primary-500"></div>
                  <span className="text-sm font-medium text-gray-700">{name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Unable to determine approval chain. Please contact your administrator.</p>
        )}

        {/* Supervisor Details (if applicable) */}
        {supervisor && !user.reportsDirectlyToDirector && user.role !== 'DIRECTOR' && (
          <div className="mt-4 rounded-lg border border-gray-200 p-4">
            <h3 className="mb-3 font-medium text-gray-900">Your Supervisor</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{supervisor.firstName} {supervisor.lastName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <a href={`mailto:${supervisor.email}`} className="font-medium text-primary-600 hover:underline">
                  {supervisor.email}
                </a>
              </div>
              {supervisor.jobTitle && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500">Position</p>
                  <p className="font-medium text-gray-900">{supervisor.jobTitle}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
